/**
 * PATH: packages/ui/src/kanban/useIsCoarsePointer.ts
 * FILE_ID: UI-KANBAN-COARSE-POINTER-001
 * PURPOSE: Live "the primary input is a finger, not a mouse" flag.
 * WHY:
 *   Card quick-actions are revealed by hover, and a tablet cannot hover, so on
 *   a tablet the rail was simply unreachable. The existing `useIsPhone`
 *   (max-width:767px) could not gate the fix: a 1024px iPad is not a phone by
 *   width, so it took the desktop hover path and got nothing. The thing that
 *   actually differs is the INPUT DEVICE, not the viewport, so that is what
 *   this asks about. Width still decides layout; this decides interaction.
 * FLOW: matchMedia('(pointer: coarse)') → subscribe → boolean
 * DEPENDENCIES: react (useEffect/useState) — matches the useIsPhone shape so
 *   the two read identically at call sites.
 * RELATED:
 *   - ./useIsPhone.ts: sibling media hook; width-based, layout decisions.
 *   - ./components/useKanbanCardInteractions.ts: main consumer.
 */
import { useEffect, useState } from 'react'

/**
 * `pointer: coarse` describes the PRIMARY pointing device. A laptop with a
 * touchscreen still reports `fine` (its primary device is the trackpad), which
 * is what we want: that machine can hover, so it should keep the hover path.
 */
const COARSE_POINTER_QUERY = '(pointer: coarse)'

export function useIsCoarsePointer(): boolean {
  const [isCoarse, setIsCoarse] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia(COARSE_POINTER_QUERY).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia(COARSE_POINTER_QUERY)
    const onChange = (e: MediaQueryListEvent) => setIsCoarse(e.matches)
    // Re-read on mount: the SSR/first-paint pass above may have guessed false,
    // and a detachable keyboard can flip this mid-session.
    setIsCoarse(mq.matches)
    // Safari < 14 only has the deprecated addListener pair — same fallback the
    // sibling width hooks carry.
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else mq.addListener(onChange)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange)
      else mq.removeListener(onChange)
    }
  }, [])

  return isCoarse
}

export default useIsCoarsePointer
